import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Search, Users2, LineChart, History } from "lucide-react";
import { LiveRosterArena } from "@/components/arena/LiveRosterArena";
import { PlayerStatsTable } from "@/components/playerrow/PlayerStatRow";
import { ValueScanner } from "@/components/value/ValueScanner";
import { LastPerformance } from "./LastPerformance";

const teamLogo = (code) => (code ? `https://a.espncdn.com/i/teamlogos/nba/500/${code.toLowerCase()}.png` : null);

const isOut = (r) => r.play_pct === 0 || /out/i.test(r.injury || "");
// Finished games: actual box score shown in the same integer cells as live.
const asLive = (r) => (r.live ? r : r.mode === "final" ? { ...r, live: { ...r.actual, min: r.min } } : r);
const liveStatsOf = (rows) => Object.fromEntries(rows.map((r) => [`${r.game_id}-${r.player_id}`, asLive(r).live]).filter(([, l]) => l));

const MODE_KEY = "nbc-projections-tab";

export function PlayersPanel({ game, games, rows, minGames, day, valueCounts, onValueChange, league = "nba", check, onCheck }) {
  const { state } = useLocation();
  const nba = league === "nba";
  const comp = nba ? undefined : league;
  // Remembers Players / Value scanner across visits; clicking the logo or menu on this page starts on Players.
  const [mode, setModeState] = useState(() => (state?.fresh ? "players" : ["value", "last"].includes(localStorage.getItem(MODE_KEY)) ? localStorage.getItem(MODE_KEY) : "players"));
  const setMode = (m) => { setModeState(m); localStorage.setItem(MODE_KEY, m); };
  useEffect(() => { if (state?.fresh) localStorage.setItem(MODE_KEY, "players"); }, [state]);
  const view = mode;
  const [team, setTeam] = useState("all");
  const [q, setQ] = useState("");
  // Default: home team first, then away, each by season minutes; clicking a column header sorts by that stat.
  const [sort, setSort] = useState({ key: "team", dir: -1 });
  const started = game && (game.status === "in" || game.status === "post");
  useEffect(() => { setTeam("all"); setSort({ key: "team", dir: -1 }); }, [game?.id]);
  const val = (r) => (sort.key === "min" ? r.min : r.stats?.[sort.key]?.proj) ?? -1;
  const list = useMemo(() => rows.filter((r) => r.game_id === game?.id && (team === "all" || r.team === team) &&
    r.name.toLowerCase().includes(q.toLowerCase()) && (started || (r.stats && !isOut(r)))).map(asLive), [rows, game, team, q, started]);
  const byTeam = (a, b) => (b.home === true) - (a.home === true) || (b.min ?? -1) - (a.min ?? -1) || a.name.localeCompare(b.name);
  const sorted = useMemo(() => (started ? list : [...list].sort(sort.key === "team" ? byTeam : (a, b) => (val(a) - val(b)) * sort.dir)), [list, sort, started]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section className="tv-panel tv-players" aria-label="Players" data-testid="tv-players-panel">
      <header className="tv-panel-head tv-players-head pt-row1">
        <div className="tv-pills tv-mode pt-tabs">
          <button type="button" className={view === "players" ? "is-active" : ""} onClick={() => setMode("players")} data-testid="tv-mode-players"><Users2 size={15} /> Players</button>
          <button type="button" className={view === "value" ? "is-active" : ""} onClick={() => setMode("value")} data-testid="tv-mode-value"><LineChart size={15} /> Value scanner</button>
          <button type="button" className={view === "last" ? "is-active" : ""} onClick={() => setMode("last")} data-testid="tv-mode-last"><History size={15} /> Last performance</button>
        </div>
        {view === "players" && <><div className="tv-pills pt-teams">
          {["all", game?.away_code, game?.home_code].filter(Boolean).map((t) => (
            <button key={t} type="button" className={team === t ? "is-active" : ""} onClick={() => setTeam(t)} data-testid={`tv-team-${t}`}>
              {t !== "all" && <img className="pt-team-logo" src={teamLogo(t)} alt="" loading="lazy" />}
              {t === "all" ? "Both teams" : t}
            </button>
          ))}
        </div>
        <label className="tv-search pt-search"><Search size={14} /><input placeholder="Search player..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="tv-search" /></label></>}
      </header>
      <div className="tv-scroll">
        {view === "last" ? <LastPerformance rows={rows} game={game} /> : view === "value" ? <ValueScanner day={day} game={game} games={games} comp={comp} valueCounts={valueCounts} onChange={onValueChange} liveStats={liveStatsOf(rows)} /> : <>
        {!game && <p className="tv-empty">Select a game.</p>}
        {game && started && <LiveRosterArena rows={sorted} embedded />}
        {game && !started && (
          <PlayerStatsTable rows={sorted} sort={sort} minGames={minGames} totals={false} teamDividers={sort.key === "team"}
            onSort={(key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : -1 }))} onCheck={onCheck} check={check} />
        )}
        {game && !sorted.length && <p className="tv-empty">No player data for this game yet.</p>}
        </>}
      </div>
    </section>
  );
}
